import type { PrequalDocumentBlock } from "@/api/prequalificationDocuments";

// Informational grey notes for the Document Language editor (owner rulings R3/R4).
// Computed client-side from the block's CURRENT text. They never block Save or
// Publish and never feed the publish override-warnings step — every blocking
// message still comes from the server validator.

export const EMPTY_BLOCK_NOTE = "Empty — this element will be left out of the document.";
export const NO_PLACEHOLDER_NOTE =
  "No placeholder — this wording is fixed and won't update automatically if the figure changes.";

/** Same grammar as the server's `MergeTokenGrammar`: a leading letter, then letters/digits. */
const TOKEN = /\{\{([A-Za-z][A-Za-z0-9]*)\}\}/g;

function tokenNames(text: string): Set<string> {
  return new Set(Array.from(text.matchAll(TOKEN), (m) => m[1]));
}

/**
 * The single grey note to show under an editable block, or null.
 * - Blank text: the "left out" note, unless the block may not be blanked
 *   (`allowEmpty === false`), where the server's red error explains it after save.
 * - Otherwise: the "no placeholder" note when the block offers placeholders and the
 *   text uses none of them, or when the text drops a placeholder its default has.
 */
export function blockNoteFor(
  block: Pick<PrequalDocumentBlock, "defaultText" | "allowedTokens" | "allowEmpty">,
  text: string,
): string | null {
  if (text.trim() === "") {
    return block.allowEmpty === false ? null : EMPTY_BLOCK_NOTE;
  }
  const used = tokenNames(text);
  const usesNoOffered =
    block.allowedTokens.length > 0 && !block.allowedTokens.some((t) => used.has(t));
  const dropsDefault = [...tokenNames(block.defaultText)].some((t) => !used.has(t));
  return usesNoOffered || dropsDefault ? NO_PLACEHOLDER_NOTE : null;
}
